/**
 * Typed wrappers for /api/attachments.
 *
 * Files are stored in Blob Storage; the browser uploads/downloads bytes
 * directly via a short-lived SAS minted by the API. The table is polymorphic
 * (EntityType + EntityId) — Contracts is the first consumer.
 */
import { apiClient } from './client';

/** Entity types that can carry attachments. Widen as the API's allow-list grows. */
export type AttachmentEntityType = 'Contract';

export interface AttachmentListItem {
  AttachmentId: number;
  EntityType: AttachmentEntityType;
  EntityId: number;
  FileName: string;
  ContentType: string | null;
  SizeBytes: number | null;
  UploadedBy: string;
  /** ISO datetime UTC. */
  UploadedDate: string;
  /** True when the caller may delete/edit this file (any oscar writer). */
  CanDelete: boolean;
  Description: string | null;
}

export interface UploadUrlRequest {
  EntityType: AttachmentEntityType;
  EntityId: number;
  FileName: string;
  ContentType: string;
  SizeBytes: number;
}

export interface UploadUrlResponse {
  UploadUrl: string;
  BlobUri: string;
  ExpiresAt: string;
}

export interface RegisterAttachmentRequest extends UploadUrlRequest {
  BlobUri: string;
  Description?: string | null;
}

export interface DownloadUrlResponse {
  DownloadUrl: string;
  ExpiresAt: string;
  FileName: string;
  ContentType: string | null;
}

/** Per-file ceiling (mirrors the API's ATTACHMENT_MAX_BYTES default). */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export async function getAttachments(
  entityType: AttachmentEntityType,
  entityId: number,
  signal?: AbortSignal,
): Promise<AttachmentListItem[]> {
  const params = new URLSearchParams({ entityType, entityId: String(entityId) });
  return apiClient.get<AttachmentListItem[]>(`/api/attachments?${params}`, signal);
}

export async function requestUploadUrl(body: UploadUrlRequest): Promise<UploadUrlResponse> {
  return apiClient.post<UploadUrlResponse>('/api/attachments/upload-url', body);
}

export async function registerAttachment(body: RegisterAttachmentRequest): Promise<AttachmentListItem> {
  return apiClient.post<AttachmentListItem>('/api/attachments', body);
}

export async function getDownloadUrl(attachmentId: number): Promise<DownloadUrlResponse> {
  return apiClient.get<DownloadUrlResponse>(`/api/attachments/${attachmentId}/download-url`);
}

export async function deleteAttachment(attachmentId: number): Promise<void> {
  return apiClient.delete<void>(`/api/attachments/${attachmentId}`);
}

/** PATCH /api/attachments/{id} — edit the description/caption (any writer). */
export async function updateAttachment(
  attachmentId: number,
  body: { Description: string | null },
): Promise<AttachmentListItem> {
  return apiClient.patch<AttachmentListItem>(`/api/attachments/${attachmentId}`, body);
}

/** True when an attachment is a previewable image (by content-type, else extension). */
export function isImageAttachment(contentType: string | null, fileName: string): boolean {
  if (contentType && contentType.toLowerCase().startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|bmp|avif|heic)$/i.test(fileName);
}
